import { useEffect, useState, useMemo, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import {
  History, Filter, RefreshCw, Loader2, Eye, X, User as UserIcon,
  LogIn, Edit2, Trash2, FileSpreadsheet, Truck, RotateCcw, FolderInput, ListChecks, AlertCircle
} from "lucide-react";

// ─── Maps & helpers ─────────────────────────────────────────────────────────
const ACTION_META = {
  login: { label: "Inicio de sesión", icon: LogIn, color: "bg-slate-100 text-slate-600 border-slate-200" },
  recalculate_totals: { label: "Recálculo totales partes", icon: RotateCcw, color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  update_work_report: { label: "Edición de parte", icon: Edit2, color: "bg-amber-50 text-amber-700 border-amber-200" },
  delete_work_report: { label: "Eliminación de parte", icon: Trash2, color: "bg-red-50 text-red-700 border-red-200" },
  import_document: { label: "Import documento → parte", icon: FolderInput, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  update_weighing_ticket: { label: "Edición de pesaje", icon: Edit2, color: "bg-amber-50 text-amber-700 border-amber-200" },
  delete_weighing_ticket: { label: "Eliminación de pesaje", icon: Trash2, color: "bg-red-50 text-red-700 border-red-200" },
  bulk_delete_weighing_tickets: { label: "Eliminación masiva pesajes", icon: Trash2, color: "bg-red-50 text-red-700 border-red-200" },
  bulk_delete_fuel_tickets: { label: "Eliminación masiva fuel", icon: Trash2, color: "bg-red-50 text-red-700 border-red-200" },
  apply_personnel_sync: { label: "Sincronización aplicada", icon: ListChecks, color: "bg-blue-50 text-blue-700 border-blue-200" },
  apply_personnel_sync_auto: { label: "Sync automática (cron)", icon: ListChecks, color: "bg-sky-50 text-sky-700 border-sky-200" },
  discard_personnel_sync: { label: "Sincronización descartada", icon: X, color: "bg-slate-100 text-slate-600 border-slate-200" },
  add_catalog_item: { label: "Alta en catálogo", icon: FileSpreadsheet, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  remove_catalog_item: { label: "Baja de catálogo", icon: FileSpreadsheet, color: "bg-rose-50 text-rose-700 border-rose-200" },
};

const fallbackMeta = { label: "Acción", icon: History, color: "bg-slate-100 text-slate-600 border-slate-200" };

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const fmtRelative = (iso) => {
  if (!iso) return "";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "ahora mismo";
    if (diff < 3600) return `hace ${Math.round(diff / 60)} min`;
    if (diff < 86400) return `hace ${Math.round(diff / 3600)} h`;
    if (diff < 86400 * 7) return `hace ${Math.round(diff / 86400)} d`;
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short" });
  } catch { return ""; }
};

// ─── Diff Modal ─────────────────────────────────────────────────────────────
function DiffModal({ entry, onClose }) {
  if (!entry) return null;
  const before = entry.before || {};
  const after = entry.after || {};
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
  const meta = ACTION_META[entry.action] || fallbackMeta;
  const Icon = meta.icon;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid="audit-diff-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${meta.color}`}>
              <Icon size={18} />
            </div>
            <div>
              <h3 className="font-bold text-slate-800">{meta.label}</h3>
              <p className="text-xs text-slate-500">{fmtDateTime(entry.timestamp)} · {entry.user_email || "—"}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1" data-testid="audit-diff-close">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {entry.description && (
            <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700">{entry.description}</div>
          )}

          {entry.contract_id && (
            <div className="text-xs text-slate-500">
              <span className="font-semibold uppercase tracking-wide">Contrato:</span>{" "}
              <span className="font-mono text-slate-700">{entry.contract_id}</span>
            </div>
          )}

          {keys.length > 0 ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Cambios</p>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-slate-600 text-xs uppercase tracking-wide">Campo</th>
                      <th className="text-left px-3 py-2 font-semibold text-rose-700 text-xs uppercase tracking-wide">Antes</th>
                      <th className="text-left px-3 py-2 font-semibold text-emerald-700 text-xs uppercase tracking-wide">Después</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keys.map((k) => {
                      const b = before[k];
                      const a = after[k];
                      const changed = JSON.stringify(b) !== JSON.stringify(a);
                      return (
                        <tr key={k} className={changed ? "bg-amber-50/50" : ""}>
                          <td className="px-3 py-2 font-mono text-slate-700 text-xs align-top">{k}</td>
                          <td className="px-3 py-2 text-xs text-slate-600 align-top break-all">{b === undefined || b === null ? "—" : JSON.stringify(b)}</td>
                          <td className="px-3 py-2 text-xs text-slate-800 align-top break-all">{a === undefined || a === null ? "—" : JSON.stringify(a)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400 italic">Sin diff de campos para esta acción.</p>
          )}

          {entry.metadata && Object.keys(entry.metadata).length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Metadatos</p>
              <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-xs overflow-x-auto">{JSON.stringify(entry.metadata, null, 2)}</pre>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-xs text-slate-500 pt-2 border-t border-slate-100">
            <div>
              <span className="font-semibold uppercase tracking-wide">Entidad:</span>{" "}
              <span className="font-mono">{entry.entity_type}{entry.entity_id ? ` · ${String(entry.entity_id).slice(0, 12)}…` : ""}</span>
            </div>
            <div>
              <span className="font-semibold uppercase tracking-wide">Rol:</span>{" "}
              <span>{entry.user_role || "—"}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tab ────────────────────────────────────────────────────────────────────
export default function AuditLogTab({ contracts = [] }) {
  const { authAxios } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actions, setActions] = useState([]);
  const [filters, setFilters] = useState({
    contract_id: "",
    action: "",
    user_email: "",
    date_from: "",
    date_to: "",
  });
  const [selected, setSelected] = useState(null);

  const fetchActions = useCallback(async () => {
    try {
      const r = await authAxios.get("/admin/audit-logs/actions");
      setActions(r.data.actions || []);
    } catch { /* opcional */ }
  }, [authAxios]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filters.contract_id) params.contract_id = filters.contract_id;
      if (filters.action) params.action = filters.action;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      params.limit = 200;
      const r = await authAxios.get("/admin/audit-logs", { params });
      let items = r.data.items || [];
      // Frontend extra filter by email (no backend filter for that)
      if (filters.user_email) {
        const q = filters.user_email.toLowerCase().trim();
        items = items.filter((x) => (x.user_email || "").toLowerCase().includes(q));
      }
      setLogs(items);
      setTotal(r.data.total || 0);
    } catch (e) {
      setError(e.response?.data?.detail || "No se pudieron cargar las entradas de auditoría");
    } finally {
      setLoading(false);
    }
  }, [authAxios, filters]);

  useEffect(() => { fetchActions(); }, [fetchActions]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const contractMap = useMemo(() => {
    const m = {};
    contracts.forEach((c) => { m[c.id] = c; });
    return m;
  }, [contracts]);

  return (
    <>
      <DiffModal entry={selected} onClose={() => setSelected(null)} />

      <div className="space-y-4 animate-fade-in-up" data-testid="audit-log-tab">
        {/* Header summary */}
        <div className="card-white p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="font-bold text-slate-800 flex items-center gap-2">
                <History size={18} className="text-indigo-600" /> Historial de auditoría
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Registro de acciones críticas: logins, recálculos, ediciones de pesajes/partes,
                sincronizaciones y cambios de catálogo. Retención automática 6 meses.
              </p>
            </div>
            <button
              onClick={fetchLogs}
              disabled={loading}
              className="text-xs font-semibold text-slate-500 hover:text-slate-800 flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              data-testid="audit-refresh-btn"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refrescar
            </button>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-sm">
            <select
              className="form-input"
              value={filters.contract_id}
              onChange={(e) => setFilters({ ...filters, contract_id: e.target.value })}
              data-testid="audit-filter-contract"
            >
              <option value="">Todos los contratos</option>
              {contracts.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>

            <select
              className="form-input"
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
              data-testid="audit-filter-action"
            >
              <option value="">Todas las acciones</option>
              {actions.map((a) => (
                <option key={a} value={a}>{ACTION_META[a]?.label || a}</option>
              ))}
            </select>

            <input
              type="email"
              placeholder="Email usuario…"
              className="form-input"
              value={filters.user_email}
              onChange={(e) => setFilters({ ...filters, user_email: e.target.value })}
              data-testid="audit-filter-email"
            />

            <input
              type="date"
              className="form-input"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              data-testid="audit-filter-date-from"
            />

            <input
              type="date"
              className="form-input"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              data-testid="audit-filter-date-to"
            />
          </div>

          <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
            <span>
              <Filter size={12} className="inline mr-1" />
              Mostrando {logs.length} de {total} entradas
            </span>
            {(filters.contract_id || filters.action || filters.user_email || filters.date_from || filters.date_to) && (
              <button
                onClick={() => setFilters({ contract_id: "", action: "", user_email: "", date_from: "", date_to: "" })}
                className="text-indigo-600 hover:text-indigo-800 font-semibold"
                data-testid="audit-clear-filters"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-700 flex items-center gap-2">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {/* Timeline */}
        <div className="card-white overflow-hidden">
          {loading && logs.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <Loader2 className="animate-spin mx-auto mb-2" size={24} />
              Cargando historial…
            </div>
          ) : logs.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <History className="mx-auto mb-2" size={32} />
              <p className="font-semibold">Sin entradas con esos filtros</p>
              <p className="text-xs mt-1">Las acciones futuras aparecerán aquí en cuanto ocurran.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="audit-list">
              {logs.map((entry) => {
                const meta = ACTION_META[entry.action] || fallbackMeta;
                const Icon = meta.icon;
                const contract = entry.contract_id ? contractMap[entry.contract_id] : null;
                return (
                  <li
                    key={entry.id}
                    className="px-5 py-3 hover:bg-slate-50/60 transition-colors cursor-pointer flex items-center gap-3"
                    onClick={() => setSelected(entry)}
                    data-testid={`audit-row-${entry.id}`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center border flex-shrink-0 ${meta.color}`}>
                      <Icon size={15} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-slate-800 truncate">{meta.label}</span>
                        {contract && (
                          <span
                            className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full"
                            style={{ background: `${contract.color || "#007934"}1a`, color: contract.color || "#007934" }}
                          >
                            {contract.name}
                          </span>
                        )}
                        <span className="text-xs text-slate-400">· {fmtRelative(entry.timestamp)}</span>
                      </div>
                      <p className="text-xs text-slate-500 truncate mt-0.5">
                        {entry.description || entry.entity_type}
                      </p>
                    </div>

                    <div className="text-right hidden md:block">
                      <p className="text-xs font-semibold text-slate-700 flex items-center gap-1 justify-end">
                        <UserIcon size={11} className="text-slate-400" /> {entry.user_email || "—"}
                      </p>
                      <p className="text-[10px] text-slate-400">{fmtDateTime(entry.timestamp)}</p>
                    </div>

                    <button
                      className="text-slate-400 hover:text-indigo-600 flex-shrink-0 p-2 rounded-lg hover:bg-indigo-50"
                      onClick={(e) => { e.stopPropagation(); setSelected(entry); }}
                      title="Ver detalle"
                      data-testid={`audit-view-${entry.id}`}
                    >
                      <Eye size={14} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
